/**
 * Room containment resolution for sketch auto-filing — POSITIONAL model.
 *
 * A room folder ("tag") belongs to whichever room polygon contains its anchor
 * (`room_group_position`). A sketch files into the FIRST tag anchored in the
 * same polygon that contains the sketch (several tags may share a room; the
 * first in the pushed folder order wins). No explicit room↔folder link is
 * consulted anywhere here.
 */

import * as THREE from 'three';
import { getRoomGroupPositionProperty } from '@pointorama/potree-shared';
import { computeEnclosedFaces, faceContainingPoint } from './wallSketchFaces';
import { pointAtArcLength } from './skeleton/arcGeometry';
import type { Viewer } from '../viewer/viewer';
import type { PointoramaRoom } from './PointoramaRoom';

/** Ray-cast point-in-polygon on the XY plane (Z ignored). */
export function pointInPolygonXY(x: number, y: number, polygon: THREE.Vector3[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function polygonAreaXY(polygon: THREE.Vector3[]): number {
  let area = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    area += (polygon[j].x + polygon[i].x) * (polygon[j].y - polygon[i].y);
  }
  return Math.abs(area / 2);
}

/** A folder's stored anchor, or null while unset (zero sentinel = unplaced). */
function anchorForFolder(folderId: string): { x: number; y: number } | null {
  try {
    const stored = getRoomGroupPositionProperty(folderId).value;
    if (stored && (stored.x !== 0 || stored.y !== 0 || stored.z !== 0)) return stored;
  } catch {
    // property service hiccup
  }
  return null;
}

/** Smallest room polygon of the floor containing the point, or null. */
export function containingRoomForPoint(viewer: Viewer, floorGroupId: string, point: { x: number; y: number }): PointoramaRoom | null {
  let best: { room: PointoramaRoom; area: number } | null = null;
  for (const room of viewer.scene.rooms) {
    if (room.groupIdentifier !== floorGroupId) continue;
    const polygon = room.getPolygon();
    if (polygon.length < 3 || !pointInPolygonXY(point.x, point.y, polygon)) continue;
    const area = polygonAreaXY(polygon);
    if (!best || area < best.area) best = { room, area };
  }
  return best?.room ?? null;
}

/** The first folder (tag) of the floor whose anchor sits inside the room. */
function firstFolderInRoom(viewer: Viewer, floorGroupId: string, room: PointoramaRoom): string | null {
  const polygon = room.getPolygon();
  if (polygon.length < 3) return null;
  for (const folder of viewer.roomLabelsLayer?.getFolders() ?? []) {
    if (folder.parentIdentifier && folder.parentIdentifier !== floorGroupId) continue;
    const anchor = anchorForFolder(folder.identifier);
    if (anchor && pointInPolygonXY(anchor.x, anchor.y, polygon)) return folder.identifier;
  }
  return null;
}

/**
 * The room folder the given 2D point files into on this floor: the first tag
 * anchored in the same room-measure polygon — or, when no measure contains
 * the point (wall-sketch-only workflows), the first tag anchored in the same
 * enclosed wall-sketch face. Null when the point sits in no tagged space.
 */
export function resolveRoomGroupForPoint(viewer: Viewer, floorGroupId: string | null, point: { x: number; y: number }): string | null {
  if (!floorGroupId) return null;
  const room = containingRoomForPoint(viewer, floorGroupId, point);
  if (room) {
    const folderId = firstFolderInRoom(viewer, floorGroupId, room);
    if (folderId) return folderId;
  }
  return firstFolderInFace(viewer, floorGroupId, point);
}

/** First tag whose anchor shares the point's enclosed wall-sketch face. */
function firstFolderInFace(viewer: Viewer, floorGroupId: string, point: { x: number; y: number }): string | null {
  const sketch = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorGroupId);
  if (!sketch) return null;
  const faces = computeEnclosedFaces(sketch);
  const face = faces ? faceContainingPoint(faces, point) : null;
  if (!face) return null;
  for (const folder of viewer.roomLabelsLayer?.getFolders() ?? []) {
    if (folder.parentIdentifier && folder.parentIdentifier !== floorGroupId) continue;
    const anchor = anchorForFolder(folder.identifier);
    if (anchor && faceContainingPoint([face], anchor)) return folder.identifier;
  }
  return null;
}

/**
 * Body-center classification point for a WALL-mounted sketch object (sink,
 * toilet, …). Its anchor is null and its attachment point lies ON the wall
 * line — inside the wall band, so room containment would always miss. The
 * body center sits on the edge at (offset + width/2), pushed off the wall
 * FACE toward the placement side by (face distance + inset + depth/2) —
 * i.e. inside the room the object protrudes into. Mirrors
 * PointoramaSketchObject.resolveFrame's wall-frame math.
 */
export function wallObjectCenterPoint(
  viewer: Viewer,
  floorGroupId: string | null,
  wall: { edgeId: string; offset: number; side: boolean; inset: number },
  width: number,
  depth: number,
): { x: number; y: number } | null {
  if (!floorGroupId) return null;
  const sketch = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorGroupId);
  const edge = sketch?.getEdge(wall.edgeId);
  if (!sketch || !edge) return null;
  const start = sketch.getNode(edge.startNodeId);
  const end = sketch.getNode(edge.endNodeId);
  if (!start || !end) return null;
  const mid = pointAtArcLength({ x: start.position.x, y: start.position.y }, { x: end.position.x, y: end.position.y }, edge.bulge, wall.offset + width / 2);
  // `side: true` is the +90°-CCW side of the edge direction (see WallPlacement),
  // which is the edge's LEFT face.
  const nx = wall.side ? -mid.tangent.y : mid.tangent.y;
  const ny = wall.side ? mid.tangent.x : -mid.tangent.x;
  const push = (wall.side ? edge.leftSize : edge.rightSize) + wall.inset + depth / 2;
  return { x: mid.point.x + nx * push, y: mid.point.y + ny * push };
}

/** How far past the wall face (m) the opening's side classification point
 *  sits — just inside the flanking room, small enough for narrow corridors. */
const OPENING_SIDE_SAMPLE_MARGIN_M = 0.05;

/**
 * The room folder for a door/window OPENING_SKETCH, picked by SIDE: the room
 * on the opening's swing/inset side wins (a door files into the room it
 * opens INTO), falling back to the room on the other side — so an opening in
 * an exterior wall files into the interior room it's attached to instead of
 * nothing. Each side is classified by a point just off that wall face at the
 * opening's midpoint. Null when neither side resolves (the caller falls back
 * to the edge's roomIds / the floor group).
 */
export function resolveRoomGroupForOpeningSpan(
  viewer: Viewer,
  floorGroupId: string | null,
  span: { edgeId: string; offset: number; length: number; swingPositive: boolean },
): string | null {
  if (!floorGroupId) return null;
  const sketch = viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorGroupId);
  const edge = sketch?.getEdge(span.edgeId);
  if (!sketch || !edge) return null;
  const start = sketch.getNode(edge.startNodeId);
  const end = sketch.getNode(edge.endNodeId);
  if (!start || !end) return null;
  const mid = pointAtArcLength({ x: start.position.x, y: start.position.y }, { x: end.position.x, y: end.position.y }, edge.bulge, span.offset + span.length / 2);
  // +90°-CCW of the local tangent = the edge's LEFT face — the side
  // `swing_positive` refers to (see OpeningFlipGizmos / frameFromWallOpeningSketch).
  const norm = { x: -mid.tangent.y, y: mid.tangent.x };
  const sidePoint = (left: boolean): { x: number; y: number } => {
    const push = (left ? (edge.leftSize ?? 0.1) : (edge.rightSize ?? 0.1)) + OPENING_SIDE_SAMPLE_MARGIN_M;
    const sign = left ? 1 : -1;
    return { x: mid.point.x + norm.x * push * sign, y: mid.point.y + norm.y * push * sign };
  };
  return resolveRoomGroupForPoint(viewer, floorGroupId, sidePoint(span.swingPositive)) ?? resolveRoomGroupForPoint(viewer, floorGroupId, sidePoint(!span.swingPositive));
}

/**
 * The room folder for an opening on a wall-sketch edge: openings sit on the
 * boundary between rooms, so containment is degenerate — the edge's own
 * roomIds (sorted for determinism) pick the adjacent room, whose first tag
 * wins. Fallback behind {@link resolveRoomGroupForOpeningSpan} (which knows
 * the opening's side and position along the edge).
 */
export function resolveRoomGroupForWallEdge(viewer: Viewer, floorGroupId: string | null, roomIds: readonly string[] | undefined): string | null {
  if (!floorGroupId || !roomIds || roomIds.length === 0) return null;
  for (const roomId of [...roomIds].sort()) {
    const room = viewer.scene.rooms.find((r) => r.identifier === roomId);
    if (!room) continue;
    const folderId = firstFolderInRoom(viewer, floorGroupId, room);
    if (folderId) return folderId;
  }
  return null;
}
